import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";

const GPS_QUEUE_KEY = "hoa_pending_gps";
type GpsQueued = { villageId: number; lat: number; lng: number; ts: number };

function loadQueue(): GpsQueued[] {
  try { return JSON.parse(localStorage.getItem(GPS_QUEUE_KEY) || "[]"); } catch { return []; }
}
function saveQueue(q: GpsQueued[]) { localStorage.setItem(GPS_QUEUE_KEY, JSON.stringify(q)); }

const TEAL = "#15403a";
const S = { input: { padding: "7px 10px", border: "1px solid #cbd5d2", borderRadius: 5, fontSize: 14, minWidth: 200 } as React.CSSProperties };

interface Item {
  id: number; name: string; description?: string; active?: boolean;
  region?: string; gps_lat?: number | null; gps_lng?: number | null;
  people_groups?: Item[];
}

type Section = "projects" | "people-groups" | "villages";

function fmtCoord(lat: number | null | undefined, lng: number | null | undefined): string {
  if (lat == null || lng == null) return "";
  return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
}

function useGps() {
  const [acquiring, setAcquiring] = useState(false);
  const [gpsErr, setGpsErr] = useState("");

  const capture = useCallback((onResult: (lat: number, lng: number, acc: number) => void) => {
    if (!navigator.geolocation) { setGpsErr("Geolocation not supported by this browser."); return; }
    setAcquiring(true); setGpsErr("");
    navigator.geolocation.getCurrentPosition(
      pos => {
        setAcquiring(false);
        onResult(pos.coords.latitude, pos.coords.longitude, pos.coords.accuracy);
      },
      err => {
        setAcquiring(false);
        setGpsErr(err.code === 1 ? "Location permission denied." : "Could not get location — try again.");
      },
      { enableHighAccuracy: true, timeout: 30000, maximumAge: 0 }
    );
  }, []);

  return { acquiring, gpsErr, capture };
}

export default function TagAdmin() {
  const [section, setSection] = useState<Section>("projects");
  const [projects, setProjects] = useState<Item[]>([]);
  const [peopleGroups, setPeopleGroups] = useState<Item[]>([]);
  const [villages, setVillages] = useState<Item[]>([]);
  const [name, setName] = useState("");
  const [secondary, setSecondary] = useState("");
  const [newLat, setNewLat] = useState<number | null>(null);
  const [newLng, setNewLng] = useState<number | null>(null);
  const [newAcc, setNewAcc] = useState<number | null>(null);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [updatingGpsId, setUpdatingGpsId] = useState<number | null>(null);
  const [pendingCount, setPendingCount] = useState(() => loadQueue().length);
  const flushing = useRef(false);

  const { acquiring, gpsErr, capture } = useGps();

  const flushQueue = useCallback(async () => {
    if (flushing.current) return;
    const q = loadQueue();
    if (!q.length || !navigator.onLine) return;
    flushing.current = true;
    const remaining: GpsQueued[] = [];
    for (const item of q) {
      try {
        await axios.patch(`/api/tags/villages/${item.villageId}`, { gps_lat: item.lat, gps_lng: item.lng });
      } catch { remaining.push(item); }
    }
    saveQueue(remaining);
    setPendingCount(remaining.length);
    flushing.current = false;
    if (remaining.length < q.length) { await load(); setMsg("Offline GPS saves synced."); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    const [p, pg, v] = await Promise.all([
      axios.get("/api/tags/projects"),
      axios.get("/api/tags/people-groups"),
      axios.get("/api/tags/villages"),
    ]);
    setProjects(p.data.projects);
    setPeopleGroups(pg.data.peopleGroups);
    setVillages(v.data.villages);
  };

  useEffect(() => {
    load().catch(() => setErr("Failed to load"));
    flushQueue();
    window.addEventListener('online', flushQueue);
    return () => window.removeEventListener('online', flushQueue);
  }, [flushQueue]);

  const captureForNew = () => {
    capture((lat, lng, acc) => { setNewLat(lat); setNewLng(lng); setNewAcc(acc); });
  };

  const captureForVillage = (villageId: number) => {
    capture(async (lat, lng) => {
      setUpdatingGpsId(null);
      if (navigator.onLine) {
        try {
          await axios.patch(`/api/tags/villages/${villageId}`, { gps_lat: lat, gps_lng: lng });
          await load();
          setMsg("GPS saved.");
        } catch { setErr("Failed to save GPS."); }
      } else {
        const q = loadQueue().filter(i => i.villageId !== villageId);
        q.push({ villageId, lat, lng, ts: Date.now() });
        saveQueue(q);
        setPendingCount(q.length);
        setMsg(`GPS captured (${lat.toFixed(6)}, ${lng.toFixed(6)}) — will save when reconnected.`);
      }
    });
  };

  const add = async (e: React.FormEvent) => {
    e.preventDefault(); setMsg(""); setErr("");
    try {
      if (section === "projects") {
        await axios.post("/api/tags/projects", { name, description: secondary });
      } else if (section === "people-groups") {
        await axios.post("/api/tags/people-groups", { name, description: secondary });
      } else {
        await axios.post("/api/tags/villages", { name, region: secondary, gps_lat: newLat, gps_lng: newLng });
      }
      setName(""); setSecondary(""); setNewLat(null); setNewLng(null); setNewAcc(null);
      await load(); setMsg("Added.");
    } catch (e: any) {
      setErr(e?.response?.data?.error || "Failed to add");
    }
  };

  const remove = async (id: number) => {
    setMsg(""); setErr("");
    const ep = section === "projects" ? "projects" : section === "people-groups" ? "people-groups" : "villages";
    try { await axios.delete(`/api/tags/${ep}/${id}`); await load(); }
    catch { setErr("Cannot delete — it may still be tagged to missionaries."); }
  };

  const tagVillagePG = async (villageId: number, pgId: number, has: boolean) => {
    if (has) await axios.delete(`/api/tags/villages/${villageId}/people-groups/${pgId}`);
    else await axios.post(`/api/tags/villages/${villageId}/people-groups/${pgId}`);
    await load();
  };

  const tabs: { key: Section; label: string }[] = [
    { key: "projects", label: "Projects" },
    { key: "people-groups", label: "People Groups" },
    { key: "villages", label: "Villages" },
  ];

  const items = section === "projects" ? projects : section === "people-groups" ? peopleGroups : villages;

  return (
    <div style={{ maxWidth: 920, margin: "0 auto", padding: 24 }}>
      <h2 style={{ color: TEAL }}>Reference Lists</h2>
      <p style={{ color: "#718096", fontSize: 13 }}>
        Manage projects, people groups, and villages for this country. Assign them to missionaries on the Missionaries page.
      </p>

      <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => { setSection(t.key); setMsg(""); setErr(""); }}
            style={{ padding: "7px 18px", borderRadius: 5, border: "1px solid #cbd5d2", cursor: "pointer",
              background: section === t.key ? TEAL : "#fff", color: section === t.key ? "#fff" : "#333",
              fontWeight: section === t.key ? 600 : 400 }}>
            {t.label}
          </button>
        ))}
      </div>

      {pendingCount > 0 && (
        <div style={{ background: "#fffbeb", color: "#92400e", border: "1px solid #fde68a", borderRadius: 5, padding: "8px 12px", marginBottom: 12 }}>
          {pendingCount} GPS location{pendingCount > 1 ? "s" : ""} captured offline — will sync automatically when you reconnect.
        </div>
      )}
      {msg && <div style={{ background: "#ebf8f2", color: "#276749", border: "1px solid #c6f6e4", borderRadius: 5, padding: "8px 12px", marginBottom: 12 }}>{msg}</div>}
      {err && <div style={{ background: "#fff5f5", color: "#c53030", border: "1px solid #fed7d7", borderRadius: 5, padding: "8px 12px", marginBottom: 12 }}>{err}</div>}
      {gpsErr && <div style={{ background: "#fffbeb", color: "#92400e", border: "1px solid #fde68a", borderRadius: 5, padding: "8px 12px", marginBottom: 12 }}>{gpsErr}</div>}

      {/* Add form */}
      <form onSubmit={add} style={{ display: "flex", gap: 10, alignItems: "flex-start", flexWrap: "wrap", marginBottom: 28 }}>
        <input placeholder={section === "villages" ? "Village name" : section === "projects" ? "Project name" : "People group name"}
          value={name} onChange={e => setName(e.target.value)} required style={S.input} />
        <input placeholder={section === "villages" ? "Region (optional)" : "Description (optional)"}
          value={secondary} onChange={e => setSecondary(e.target.value)} style={S.input} />

        {section === "villages" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <button type="button" onClick={captureForNew} disabled={acquiring}
              style={{ background: acquiring ? "#718096" : "#2b6cb0", color: "#fff", border: "none",
                padding: "8px 14px", borderRadius: 5, cursor: acquiring ? "default" : "pointer", fontSize: 13, whiteSpace: "nowrap" }}>
              {acquiring ? "Acquiring GPS…" : "📍 Capture Location"}
            </button>
            {newLat != null && newLng != null && (
              <div style={{ fontSize: 12, color: "#2d7a5e" }}>
                {fmtCoord(newLat, newLng)}
                {newAcc != null && <span style={{ color: "#718096" }}> ±{Math.round(newAcc)}m</span>}
              </div>
            )}
          </div>
        )}

        <button type="submit" style={{ background: TEAL, color: "#fff", border: "none", padding: "8px 16px", borderRadius: 5, cursor: "pointer" }}>
          Add
        </button>
      </form>

      {/* List */}
      {items.length === 0 ? (
        <p style={{ color: "#a0aec0", fontStyle: "italic" }}>None yet.</p>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
          <thead>
            <tr style={{ color: "#718096", textAlign: "left" }}>
              <th style={{ padding: "6px 8px" }}>Name</th>
              <th style={{ padding: "6px 8px" }}>{section === "villages" ? "Region" : "Description"}</th>
              {section === "villages" && <th style={{ padding: "6px 8px" }}>Coordinates</th>}
              {section === "villages" && <th style={{ padding: "6px 8px" }}>People Groups</th>}
              <th style={{ padding: "6px 8px" }}></th>
            </tr>
          </thead>
          <tbody>
            {items.map(item => (
              <tr key={item.id} style={{ borderTop: "1px solid #eef2f1", verticalAlign: "top" }}>
                <td style={{ padding: "10px 8px", fontWeight: 500 }}>{item.name}</td>
                <td style={{ padding: "10px 8px", color: "#718096" }}>{item.region || item.description || "—"}</td>

                {section === "villages" && (
                  <td style={{ padding: "10px 8px", minWidth: 160 }}>
                    {updatingGpsId === item.id ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        <button type="button" onClick={() => captureForVillage(item.id)} disabled={acquiring}
                          style={{ background: acquiring ? "#718096" : "#2b6cb0", color: "#fff", border: "none",
                            padding: "5px 10px", borderRadius: 4, cursor: acquiring ? "default" : "pointer", fontSize: 12 }}>
                          {acquiring ? "Acquiring…" : "📍 Capture now"}
                        </button>
                        <button type="button" onClick={() => setUpdatingGpsId(null)}
                          style={{ background: "none", border: "none", color: "#718096", cursor: "pointer", fontSize: 12 }}>
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div>
                        {item.gps_lat != null ? (
                          <div style={{ fontSize: 12, fontFamily: "monospace", color: "#2d3748" }}>
                            {fmtCoord(item.gps_lat, item.gps_lng)}
                          </div>
                        ) : (
                          <span style={{ fontSize: 12, color: "#a0aec0" }}>No GPS</span>
                        )}
                        <button type="button" onClick={() => setUpdatingGpsId(item.id)}
                          style={{ background: "none", border: "none", color: "#2b6cb0", cursor: "pointer",
                            fontSize: 12, padding: 0, marginTop: 2 }}>
                          {item.gps_lat != null ? "Update GPS" : "Set GPS"}
                        </button>
                      </div>
                    )}
                  </td>
                )}

                {section === "villages" && (
                  <td style={{ padding: "10px 8px" }}>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                      {peopleGroups.map(pg => {
                        const has = item.people_groups?.some(p => p.id === pg.id) ?? false;
                        return (
                          <span key={pg.id} onClick={() => tagVillagePG(item.id, pg.id, has)}
                            style={{ cursor: "pointer", padding: "2px 8px", borderRadius: 12, fontSize: 12,
                              background: has ? "#2d7a5e" : "#eef2f1", color: has ? "#fff" : "#555",
                              border: `1px solid ${has ? "#2d7a5e" : "#cbd5d2"}` }}>
                            {pg.name}
                          </span>
                        );
                      })}
                      {peopleGroups.length === 0 && <span style={{ color: "#a0aec0", fontSize: 12 }}>Add people groups first</span>}
                    </div>
                  </td>
                )}

                <td style={{ padding: "10px 8px" }}>
                  <button onClick={() => remove(item.id)}
                    style={{ background: "none", border: "none", color: "#e53e3e", cursor: "pointer", fontSize: 13 }}>
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
